import { createFileRoute, Link, Outlet, redirect, useMatchRoute } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { CommandPalette, useCommandPalette } from '@/components/app/command-palette'
import { MAIN_NAV, SECONDARY_NAV, type NavItem } from '@/components/app/nav'
import { UserMenu } from '@/components/app/user-menu'
import { WorkspaceSwitcher } from '@/components/app/workspace-switcher'
import { LogoMark } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { authClient } from '@/lib/auth-client'
import { sessionQuery, workspacesQuery } from '@/lib/session'

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(sessionQuery).catch(() => null)
    if (!session) throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    if (session.user.deletionScheduledFor) throw redirect({ to: '/account-deletion' })
    const workspaces = await context.queryClient.fetchQuery(workspacesQuery)
    if (workspaces.length === 0) throw redirect({ to: '/onboarding' })
    if (!workspaces.some((w) => w.id === session.session.activeOrganizationId)) {
      await authClient.organization.setActive({ organizationId: workspaces[0]!.id })
      await context.queryClient.invalidateQueries({ queryKey: ['session'] })
    }
  },
  component: AppLayout,
})

function AppLayout() {
  const [paletteOpen, setPaletteOpen] = useCommandPalette()
  return (
    // App shell: the viewport never scrolls. The sidebar is fixed in place and
    // only the content pane (SidebarInset) scrolls, so nothing can drift.
    <SidebarProvider className="h-svh overflow-hidden">
      <a
        href="#main"
        className="bg-primary text-primary-foreground sr-only z-50 rounded-md px-3 py-2 focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Skip to content
      </a>
      <AppSidebar onSearch={() => setPaletteOpen(true)} />
      <SidebarInset id="app-scroll" className="bg-background min-h-0 overflow-y-auto overscroll-contain">
        {/* Phones only: on desktop the sidebar carries navigation and search. */}
        <header className="bg-background/85 sticky top-0 z-20 flex h-14 min-h-14 items-center gap-2 px-4 backdrop-blur-md md:hidden">
          <SidebarTrigger className="-ml-1" />
          <Link to="/" aria-label="Nebueye home">
            <LogoMark className="size-6" />
          </Link>
          <Button variant="ghost" size="icon" className="ml-auto" onClick={() => setPaletteOpen(true)} aria-label="Search or jump to a page">
            <Search />
          </Button>
        </header>
        <div id="main" className="mx-auto w-full max-w-[1600px] flex-1 px-4 pt-6 pb-16 sm:px-8 sm:pt-8">
          <Outlet />
        </div>
      </SidebarInset>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </SidebarProvider>
  )
}

function AppSidebar({ onSearch }: { onSearch: () => void }) {
  return (
    <Sidebar collapsible="offcanvas" className="border-r-0">
      <SidebarHeader className="gap-3 px-3 pt-4">
        <WorkspaceSwitcher />
        <button
          type="button"
          onClick={onSearch}
          className="bg-card text-muted-foreground hover:text-foreground hover:border-border-strong flex h-9 w-full items-center gap-2 rounded-md border px-2.5 text-[13px] shadow-xs transition-colors"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">Search</span>
          <kbd className="bg-surface-sunken text-subtle rounded px-1.5 py-0.5 font-sans text-[11px]">⌘K</kbd>
        </button>
      </SidebarHeader>
      <SidebarContent className="px-1">
        <SidebarGroup>
          <SidebarGroupContent>
            <NavList items={MAIN_NAV} />
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <NavList items={SECONDARY_NAV} />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="px-3 pb-4">
        <UserMenu />
      </SidebarFooter>
    </Sidebar>
  )
}

function NavList({ items }: { items: NavItem[] }) {
  const matchRoute = useMatchRoute()
  const { setOpenMobile } = useSidebar()
  return (
    <SidebarMenu className="gap-0.5">
      {items.map((item) => {
        const active = !!matchRoute({ to: item.to, fuzzy: item.to !== '/' })
        return (
          <SidebarMenuItem key={item.to}>
            <SidebarMenuButton
              asChild
              isActive={active}
              className="data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground h-9 text-[13.5px] data-[active=true]:font-medium"
            >
              <Link to={item.to} onClick={() => setOpenMobile(false)}>
                <item.icon className="size-[18px]" strokeWidth={active ? 2.25 : 1.75} />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
            {item.soon && (
              <SidebarMenuBadge className="text-subtle bg-surface-raised dark:bg-sidebar-accent rounded-full px-1.5 text-[10px] font-medium tracking-wide uppercase">
                Soon
              </SidebarMenuBadge>
            )}
          </SidebarMenuItem>
        )
      })}
    </SidebarMenu>
  )
}
